import { create } from 'zustand';
import { collection, addDoc, updateDoc, deleteDoc, doc, getDocs, query, where, getDoc } from 'firebase/firestore';
import type { Beneficiaire } from '../types/beneficiaire';
import { db, auth } from '../config/firebase';
import { supabase } from '../config/supabase';
import type { JobOffer } from '../types/jobOffer';
import type { Proposition } from '../types/convention';
import { toast } from 'react-hot-toast';
import { withCrmType, isCurrentCrmType } from '../utils/crmType';

function normalizeName(s: string): string {
  return (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
}

async function fetchBeneficiaire(beneficiaireId: string): Promise<Beneficiaire | null> {
  const { useBeneficiairesStore } = await import('../stores/beneficiairesStore');
  const cached = useBeneficiairesStore.getState().beneficiaires.find(b => b.id === beneficiaireId);
  if (cached) return cached;
  try {
    const docRef = doc(db, 'beneficiaires', beneficiaireId);
    const snap = await getDoc(docRef);
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() } as Beneficiaire;
  } catch {
    return null;
  }
}

async function findEmployee(beneficiaire: Beneficiaire): Promise<{ id: string } | null> {
  const bEmail = (beneficiaire.email || '').toLowerCase().trim();
  const bFirst = normalizeName(beneficiaire.firstName);
  const bLast = normalizeName(beneficiaire.lastName);

  if (bEmail) {
    const { data } = await supabase
      .from('convention_employees')
      .select('id, propositions')
      .ilike('email', bEmail)
      .limit(1);
    if (data && data.length > 0) return data[0] as { id: string; propositions: Proposition[] };
  }

  if (bFirst && bLast) {
    // Names are compared accent-insensitively in JS since ilike does not ignore accents.
    const { data } = await supabase
      .from('convention_employees')
      .select('id, prenom, nom, propositions');
    const match = (data || []).find(e =>
      normalizeName(e.nom) === bLast && normalizeName(e.prenom) === bFirst
    ) || (data || []).find(e =>
      normalizeName(e.nom).includes(bLast) && normalizeName(e.prenom).includes(bFirst)
    );
    if (match) return { id: match.id, propositions: match.propositions || [] } as { id: string; propositions: Proposition[] };
  }

  return null;
}

async function syncEmployeeToReclassement(
  beneficiaireId: string,
  offer: JobOffer,
  status: 'proposed' | 'placed',
): Promise<void> {
  const beneficiaire = await fetchBeneficiaire(beneficiaireId);
  if (!beneficiaire) return;

  const emp = await findEmployee(beneficiaire);
  if (!emp) throw new Error('Candidat introuvable dans le reclassement');

  const existingProps: Proposition[] = (emp as any).propositions || [];
  const offerId = offer.id || '';
  const propIndex = existingProps.findIndex(p =>
    p.offerId === offerId ||
    (!offerId && p.entreprise === (offer.company || '') && p.poste === (offer.title || ''))
  );

  const newProp: Proposition = {
    offerId,
    entreprise: offer.company || '',
    poste: offer.title || '',
    contrat: offer.type || '',
    cv_envoye: status === 'proposed' ? new Date().toLocaleDateString('fr-FR') : (propIndex >= 0 ? existingProps[propIndex].cv_envoye : ''),
    entretien: propIndex >= 0 ? existingProps[propIndex].entretien : '',
    resultat: propIndex >= 0 ? existingProps[propIndex].resultat : '',
    ove: propIndex >= 0 ? existingProps[propIndex].ove : '',
    notes: propIndex >= 0 ? existingProps[propIndex].notes : '',
  };

  let updatedProps: Proposition[];
  if (propIndex >= 0) {
    updatedProps = existingProps.map((p, i) => i === propIndex ? newProp : p);
  } else {
    updatedProps = [...existingProps, newProp];
  }

  const updates: Record<string, unknown> = {
    propositions: updatedProps,
    entreprise: updatedProps[0]?.entreprise || '',
    poste: updatedProps[0]?.poste || '',
    contrat: updatedProps[0]?.contrat || '',
    cv_envoye: updatedProps[0]?.cv_envoye || '',
    entretien: updatedProps[0]?.entretien || '',
    resultat: updatedProps[0]?.resultat || '',
    ove: updatedProps[0]?.ove || '',
    updated_at: new Date().toISOString(),
  };

  if (status === 'proposed') {
    updates.statut = 'En cours';
  } else {
    updates.statut = 'Reclassé';
  }

  const { error } = await supabase.from('convention_employees').update(updates).eq('id', emp.id);
  if (error) throw error;

  const { useConventionsStore } = await import('../stores/conventionsStore');
  await useConventionsStore.getState().loadAllEmployees();
}

async function resetEmployeeFromReclassement(
  beneficiaireId: string,
  offerId: string,
): Promise<void> {
  const beneficiaire = await fetchBeneficiaire(beneficiaireId);
  if (!beneficiaire) return;

  const emp = await findEmployee(beneficiaire);
  if (!emp) return;

  const existingProps: Proposition[] = (emp as any).propositions || [];
  const offer = useOffersStore.getState().offers.find(o => o.id === offerId);
  const offerCompany = offer?.company || '';
  const offerTitle = offer?.title || '';

  const updatedProps = existingProps.filter(p =>
    p.offerId !== offerId &&
    !(p.offerId === '' && p.entreprise === offerCompany && p.poste === offerTitle)
  );

  const updates: Record<string, unknown> = {
    propositions: updatedProps,
    entreprise: updatedProps[0]?.entreprise || '',
    poste: updatedProps[0]?.poste || '',
    contrat: updatedProps[0]?.contrat || '',
    cv_envoye: updatedProps[0]?.cv_envoye || '',
    entretien: updatedProps[0]?.entretien || '',
    resultat: updatedProps[0]?.resultat || '',
    ove: updatedProps[0]?.ove || '',
    updated_at: new Date().toISOString(),
  };

  if (updatedProps.length === 0) {
    updates.statut = 'En recherche d\'emploi';
  }

  const { error } = await supabase
    .from('convention_employees')
    .update(updates)
    .eq('id', emp.id);
  if (error) throw error;

  const { useConventionsStore } = await import('../stores/conventionsStore');
  await useConventionsStore.getState().loadAllEmployees();
}

export interface AiJobOffer {
  id: string;
  external_id: string;
  title: string;
  company: string;
  location: string;
  type: string;
  description: string;
  salary: string;
  duration: string;
  skills: string[];
  experience: string;
  external_url: string;
  source: string;
  matched_keyword: string;
  imported_to_offers: boolean;
  created_at: string;
}

interface OffersState {
  offers: JobOffer[];
  aiOffers: AiJobOffer[];
  isAiScraping: boolean;
  isLoading: boolean;
  error: string | null;
  addOffer: (offer: Omit<JobOffer, 'id' | 'createdAt' | 'candidates'>) => Promise<void>;
  updateOffer: (id: string, offer: Partial<JobOffer>) => Promise<void>;
  deleteOffer: (id: string) => Promise<void>;
  deleteAllOffers: () => Promise<void>;
  loadOffers: () => Promise<void>;
  addCandidate: (offerId: string, beneficiaireId: string) => Promise<void>;
  removeCandidate: (offerId: string, beneficiaireId: string) => Promise<void>;
  placeCandidate: (offerId: string, beneficiaireId: string) => Promise<void>;
  seedDemoOffers: (beneficiaireIds: string[]) => void;
  scrapeAiOffers: (keywords: string[], communes?: string[]) => Promise<{
    found: number;
    inserted: number;
    alreadyExisting: number;
    foundBySource: Record<string, number>;
    adzunaConfigured: boolean;
    warnings: string[];
  }>;
  loadAiOffers: () => Promise<void>;
  importAiOffer: (aiOfferId: string) => Promise<string | undefined>;
  importAllAiOffers: () => Promise<number>;
}

export const useOffersStore = create<OffersState>((set, get) => ({
  offers: [],
  aiOffers: [],
  isAiScraping: false,
  isLoading: false,
  error: null,

  addOffer: async (offer) => {
    if (!auth.currentUser) {
      throw new Error('Authentification requise');
    }

    try {
      const newOffer = withCrmType({
        ...offer,
        userId: auth.currentUser.uid,
        candidates: [],
        createdAt: new Date().toISOString(),
        createdBy: offer.createdBy || auth.currentUser?.displayName || 'Utilisateur inconnu'
      });

      const docRef = await addDoc(collection(db, 'offers'), newOffer);
      
      set(state => ({
        offers: [...state.offers, { ...newOffer, id: docRef.id }],
        error: null
      }));

      toast.success('Offre ajoutée avec succès');
    } catch (error) {
      console.error('Error adding offer:', error);
      const message = 'Erreur lors de l\'ajout de l\'offre';
      set({ error: message });
      toast.error(message);
      throw error;
    }
  },

  updateOffer: async (id, updates) => {
    if (!auth.currentUser) {
      throw new Error('Authentification requise');
    }

    try {
      const docRef = doc(db, 'offers', id);
      await updateDoc(docRef, updates);

      set(state => ({
        offers: state.offers.map(o => 
          o.id === id ? { ...o, ...updates } : o
        ),
        error: null
      }));

      toast.success('Offre mise à jour avec succès');
    } catch (error) {
      console.error('Error updating offer:', error);
      const message = 'Erreur lors de la mise à jour de l\'offre';
      set({ error: message });
      toast.error(message);
      throw error;
    }
  },

  deleteOffer: async (id) => {
    if (!auth.currentUser) {
      throw new Error('Authentification requise');
    }

    try {
      await deleteDoc(doc(db, 'offers', id));
      set(state => ({
        offers: state.offers.filter(o => o.id !== id),
        error: null
      }));
      toast.success('Offre supprimée avec succès');
    } catch (error) {
      console.error('Error deleting offer:', error);
      const message = 'Erreur lors de la suppression de l\'offre';
      set({ error: message });
      toast.error(message);
      throw error;
    }
  },

  deleteAllOffers: async () => {
    if (!auth.currentUser) {
      throw new Error('Authentification requise');
    }

    try {
      const snapshot = await getDocs(collection(db, 'offers'));
      const toDelete = snapshot.docs.filter(d => isCurrentCrmType(d.data() as any));
      await Promise.all(toDelete.map(d => deleteDoc(doc(db, 'offers', d.id))));
      set({ offers: [], error: null });
      toast.success('Toutes les offres ont été supprimées');
    } catch (error) {
      console.error('Error deleting all offers:', error);
      const message = 'Erreur lors de la suppression';
      set({ error: message });
      toast.error(message);
      throw error;
    }
  },

  loadOffers: async () => {
    if (!auth.currentUser) {
      set({ offers: [], error: null });
      return;
    }

    set({ isLoading: true, error: null });
    try {
      // Load all offers without filtering by userId to allow sharing between roles
      const q = query(collection(db, 'offers'));
      const querySnapshot = await getDocs(q);
      const offers = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        candidates: doc.data().candidates || []
      })).filter(o => isCurrentCrmType(o)) as JobOffer[];
      
      set({ offers, isLoading: false, error: null });
    } catch (error) {
      console.error('Error loading offers:', error);
      const message = 'Erreur lors du chargement des offres';
      set({ isLoading: false, error: message });
      toast.error(message);
      throw error;
    }
  },

  addCandidate: async (offerId: string, beneficiaireId: string) => {
    if (!auth.currentUser) {
      throw new Error('Authentification requise');
    }

    try {
      const docRef = doc(db, 'offers', offerId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        throw new Error('Offre non trouvée');
      }

      const currentOffer = { id: docSnap.id, ...docSnap.data() } as JobOffer;
      const currentCandidates = currentOffer.candidates || [];

      if (currentCandidates.some(c => c.beneficiaireId === beneficiaireId)) {
        throw new Error('Ce candidat est déjà ajouté à cette offre');
      }

      const newCandidate = {
        beneficiaireId,
        status: 'proposed' as const,
        date: new Date().toISOString()
      };

      await updateDoc(docRef, {
        candidates: [...currentCandidates, newCandidate]
      });

      set(state => ({
        offers: state.offers.map(o => 
          o.id === offerId 
            ? { ...o, candidates: [...o.candidates, newCandidate] }
            : o
        ),
        error: null
      }));

      try {
        await syncEmployeeToReclassement(beneficiaireId, currentOffer, 'proposed');
      } catch (syncErr) {
        console.warn('Reclassement sync failed:', syncErr);
        toast.error('Candidat ajouté, mais la mise à jour du reclassement a échoué');
      }

      toast.success('Candidat ajouté avec succès');
    } catch (error) {
      console.error('Error adding candidate:', error);
      const message = 'Erreur lors de l\'ajout du candidat';
      set({ error: message });
      toast.error(message);
      throw error;
    }
  },

  removeCandidate: async (offerId: string, beneficiaireId: string) => {
    if (!auth.currentUser) {
      throw new Error('Authentification requise');
    }

    try {
      const docRef = doc(db, 'offers', offerId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        throw new Error('Offre non trouvée');
      }

      const currentOffer = { id: docSnap.id, ...docSnap.data() } as JobOffer;
      const updatedCandidates = currentOffer.candidates.filter(
        c => c.beneficiaireId !== beneficiaireId
      );

      await updateDoc(docRef, {
        candidates: updatedCandidates
      });

      set(state => ({
        offers: state.offers.map(o => 
          o.id === offerId 
            ? { ...o, candidates: updatedCandidates }
            : o
        ),
        error: null
      }));

      try {
        await resetEmployeeFromReclassement(beneficiaireId, offerId);
      } catch (syncErr) {
        console.warn('Reclassement reset failed:', syncErr);
        toast.error('Candidat retiré, mais la mise à jour du reclassement a échoué');
      }

      toast.success('Candidat retiré avec succès');
    } catch (error) {
      console.error('Error removing candidate:', error);
      const message = 'Erreur lors de la suppression du candidat';
      set({ error: message });
      toast.error(message);
      throw error;
    }
  },

  placeCandidate: async (offerId: string, beneficiaireId: string) => {
    if (!auth.currentUser) {
      throw new Error('Authentification requise');
    }

    try {
      const docRef = doc(db, 'offers', offerId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        throw new Error('Offre non trouvée');
      }

      const currentOffer = { id: docSnap.id, ...docSnap.data() } as JobOffer;
      const updatedCandidates = currentOffer.candidates.map(c => 
        c.beneficiaireId === beneficiaireId 
          ? { ...c, status: 'placed' as const }
          : c
      );

      await updateDoc(docRef, {
        candidates: updatedCandidates,
        status: 'filled'
      });

      set(state => ({
        offers: state.offers.map(o =>
          o.id === offerId
            ? { ...o, candidates: updatedCandidates, status: 'filled' }
            : o
        ),
        error: null
      }));

      try {
        await syncEmployeeToReclassement(beneficiaireId, currentOffer, 'placed');
      } catch (syncErr) {
        console.warn('Reclassement sync failed:', syncErr);
        toast.error('Candidat placé, mais la mise à jour du reclassement a échoué');
      }

      toast.success('Candidat placé avec succès');
    } catch (error) {
      console.error('Error placing candidate:', error);
      const message = 'Erreur lors du placement du candidat';
      set({ error: message });
      toast.error(message);
      throw error;
    }
  },

  scrapeAiOffers: async (keywords: string[], departements: string[] = []) => {
    set({ isAiScraping: true });
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      const response = await fetch(`${supabaseUrl}/functions/v1/ai-job-scraper`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${anonKey}`,
        },
        body: JSON.stringify({ keywords, departement: departements[0] || '', maxResults: 50 }),
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `Erreur ${response.status}`);
      }
      const result = await response.json();
      await get().loadAiOffers();
      set({ isAiScraping: false });
      return {
        found: result.found || 0,
        inserted: result.inserted || 0,
        alreadyExisting: result.alreadyExisting || 0,
        foundBySource: result.foundBySource && typeof result.foundBySource === 'object' ? result.foundBySource : {},
        adzunaConfigured: result.adzunaConfigured === true,
        warnings: Array.isArray(result.warnings) ? result.warnings : [],
      };
    } catch (error) {
      console.error('AI scrape error:', error);
      set({ isAiScraping: false });
      throw error;
    }
  },

  loadAiOffers: async () => {
    try {
      const { data, error } = await supabase
        .from('ai_job_offers')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      set({ aiOffers: (data as AiJobOffer[]) || [] });
    } catch (error) {
      console.error('Error loading AI offers:', error);
    }
  },

  importAllAiOffers: async () => {
    if (!auth.currentUser) throw new Error('Authentification requise');
    const pending = get().aiOffers.filter(o => !o.imported_to_offers);
    if (pending.length === 0) return 0;

    let imported = 0;
    for (const aiOffer of pending) {
      try {
        const newOffer = withCrmType({
          title: aiOffer.title,
          company: aiOffer.company,
          location: aiOffer.location || '',
          type: aiOffer.type || 'CDD',
          urgencyLevel: 'medium' as const,
          status: 'new' as const,
          transmissionStatus: 'jobboard' as const,
          description: aiOffer.description || '',
          salary: aiOffer.salary || '',
          duration: aiOffer.duration || '',
          candidates: [],
          createdAt: new Date().toISOString(),
          createdBy: 'IA',
          userId: auth.currentUser!.uid,
          isAiGenerated: true,
          aiSource: aiOffer.source || 'France Travail',
          aiMatchedPoste: aiOffer.matched_keyword || '',
          skills: aiOffer.skills || [],
        });

        const docRef = await addDoc(collection(db, 'offers'), newOffer);

        await supabase
          .from('ai_job_offers')
          .update({ imported_to_offers: true })
          .eq('id', aiOffer.id);

        set(state => ({
          offers: [...state.offers, { ...newOffer, id: docRef.id }],
          aiOffers: state.aiOffers.map(o => o.id === aiOffer.id ? { ...o, imported_to_offers: true } : o),
        }));
        imported++;
      } catch (err) {
        console.error('Error importing AI offer:', aiOffer.id, err);
      }
    }

    toast.success(`${imported} offre(s) IA importée(s) avec succès`);
    return imported;
  },

  importAiOffer: async (aiOfferId: string) => {
    const aiOffer = get().aiOffers.find(o => o.id === aiOfferId);
    if (!aiOffer) return;
    if (!auth.currentUser) throw new Error('Authentification requise');

    try {
      const newOffer = withCrmType({
        title: aiOffer.title,
        company: aiOffer.company,
        location: aiOffer.location || '',
        type: aiOffer.type || 'CDD',
        urgencyLevel: 'medium' as const,
        status: 'new' as const,
        transmissionStatus: 'jobboard' as const,
        description: aiOffer.description || '',
        salary: aiOffer.salary || '',
        duration: aiOffer.duration || '',
        candidates: [],
        createdAt: new Date().toISOString(),
        createdBy: 'IA',
        userId: auth.currentUser.uid,
        isAiGenerated: true,
        aiSource: aiOffer.source || 'France Travail',
        aiMatchedPoste: aiOffer.matched_keyword || '',
        skills: aiOffer.skills || [],
        sourceUrl: aiOffer.external_url || '',
      });

      const docRef = await addDoc(collection(db, 'offers'), newOffer);

      await supabase
        .from('ai_job_offers')
        .update({ imported_to_offers: true })
        .eq('id', aiOfferId);

      set(state => ({
        offers: [...state.offers, { ...newOffer, id: docRef.id }],
        aiOffers: state.aiOffers.map(o => o.id === aiOfferId ? { ...o, imported_to_offers: true } : o),
      }));

      toast.success('Offre IA importée avec succès');
      return docRef.id;
    } catch (error) {
      console.error('Error importing AI offer:', error);
      toast.error('Erreur lors de l\'import de l\'offre IA');
      throw error;
    }
  },

  seedDemoOffers: (beneficiaireIds: string[]) => {
    const now = new Date().toISOString();
    const mkCandidates = (count: number, startIdx: number) =>
      beneficiaireIds.slice(startIdx, startIdx + count).map((id, i) => ({
        beneficiaireId: id,
        status: (i === 0 ? 'placed' : 'proposed') as 'placed' | 'proposed',
        date: new Date(Date.now() - (i + 1) * 86400000).toISOString(),
      }));

    const demoOffers: JobOffer[] = [
      {
        id: 'demo-offer-1',
        title: 'Chef de projet digital',
        company: 'Groupe Lemoine SA',
        location: 'Lyon (69)',
        type: 'CDI',
        urgencyLevel: 'high',
        status: 'open',
        transmissionStatus: 'ove',
        oveCriteria: {
          contractType: 'CDI',
          salary: '45 000 € – 52 000 € brut annuel',
          maxSalaryDrop: '10%',
          geographicZone: 'Lyon et agglomération — 30 km max',
          maxCommuteKm: '30',
          maxCommuteMinutes: '45',
          skillsMatch: 'Gestion de projet agile, Jira, méthodologie SAFe',
        },
        description: 'Pilotage d\'un programme de transformation digitale de 3 ans. Vous coordonnerez 4 équipes (15 personnes), en lien direct avec la DSI et la direction métier. Budget projet : 2,5 M€.\n\nMissions principales :\n• Définir et suivre la roadmap projet\n• Animer les cérémonies agile (daily, sprint review, retrospective)\n• Gérer le budget et le reporting auprès du comité de direction\n• Coordonner les prestataires externes\n\nProfil recherché :\n• 7+ ans d\'expérience en gestion de projet IT\n• Certification PMP ou Prince2 souhaitée\n• Anglais courant indispensable',
        salary: '48 000 € brut annuel',
        duration: 'indeterminee',
        contact: {
          name: 'Sophie Marchand',
          role: 'Directrice des Ressources Humaines',
          email: 's.marchand@groupe-lemoine.fr',
          phone: '04 78 32 14 05',
        },
        candidates: mkCandidates(3, 0),
        createdAt: now,
        createdBy: 'Démo ANTHEA',
        requirements: ['PMP ou Prince2', '7+ ans expérience', 'Anglais courant'],
        benefits: ['Véhicule de fonction', 'Télétravail 2j/sem', 'Participation aux bénéfices'],
        skills: ['Agile', 'Jira', 'SAFe', 'Budget', 'Reporting'],
      },
      {
        id: 'demo-offer-2',
        title: 'Responsable comptable',
        company: 'Industrie Verot & Fils',
        location: 'Saint-Étienne (42)',
        type: 'CDI',
        urgencyLevel: 'medium',
        status: 'open',
        transmissionStatus: 'prospection',
        description: 'Rejoignez notre direction financière en tant que Responsable Comptable. Vous supervisez une équipe de 4 personnes et reportez directement au DAF.\n\nMissions :\n• Clôture mensuelle et annuelle des comptes\n• Déclarations fiscales et sociales\n• Supervision de la paie (120 salariés)\n• Relation avec le commissaire aux comptes\n• Mise en place d\'un nouvel outil ERP',
        salary: '40 000 € – 45 000 € brut annuel',
        duration: 'indeterminee',
        contact: {
          name: 'Pierre Verot',
          role: 'Directeur Général',
          email: 'p.verot@verot-fils.fr',
          phone: '04 77 92 10 00',
        },
        candidates: mkCandidates(2, 3),
        createdAt: now,
        createdBy: 'Démo ANTHEA',
        requirements: ['DCG ou DESC', '5+ ans expérience', 'Maîtrise Sage'],
        benefits: ['Mutelle 80%', 'Tickets restaurant', '13ème mois'],
        skills: ['Comptabilité', 'Fiscalité', 'Paie', 'Sage', 'ERP'],
      },
      {
        id: 'demo-offer-3',
        title: 'Développeur Full-Stack React / Node.js',
        company: 'TechNova Solutions',
        location: 'Télétravail / Paris (75)',
        type: 'CDD',
        urgencyLevel: 'high',
        status: 'new',
        transmissionStatus: 'jobboard',
        description: 'Dans le cadre d\'un projet de refonte de plateforme e-commerce, nous recherchons un développeur Full-Stack pour une durée de 6 mois (renouvelable).\n\nStack technique :\n• Frontend : React 18, TypeScript, Tailwind CSS\n• Backend : Node.js, Express, PostgreSQL\n• DevOps : Docker, AWS, CI/CD GitHub Actions\n\nVous travaillerez en équipe agile (squad de 5 développeurs) sur le passage d\'une architecture monolithique vers micro-services.',
        salary: '55 000 € brut annuel (pro rata)',
        duration: '6',
        contact: {
          name: 'Karim Benali',
          role: 'CTO',
          email: 'k.benali@technova.io',
          phone: '01 45 33 88 12',
        },
        candidates: mkCandidates(4, 5),
        createdAt: now,
        createdBy: 'Démo ANTHEA',
        requirements: ['5+ ans React', '3+ ans Node.js', 'Expérience AWS'],
        benefits: ['Télétravail total', 'Matériel au choix', 'Budget formation 2000€'],
        skills: ['React', 'TypeScript', 'Node.js', 'PostgreSQL', 'AWS', 'Docker'],
      },
    ];

    set(state => ({
      offers: [...demoOffers, ...state.offers.filter(o => !o.id.startsWith('demo-offer-'))],
      error: null,
    }));
    toast.success('3 offres de démonstration ajoutées');
  }
}));